import {
  DEFAULT_PRETERITE, DEFAULT_REGULAR, DEFAULT_STEM, ENGLISH, IRREGULAR, PEOPLE, PERSON_LABEL,
  PRETERITE_IRREGULAR, STEM_CHANGERS, endingOf, present, presentUnchanged, preterite, type Person,
} from './conjugate';
import { LIKED, LIKERS, PRONOUNS } from './gustar';
import { NOUNS, article } from './nouns';
import { SER_ESTAR } from './serEstar';
import { pick, shuffle } from './util';
import { enArticles, enBe, enPastIrregular, enPastRegular, enPresentS } from './english';
import { frEr, frEtreAvoir, frGender, frIr, frPasseCompose, frRe } from './french';
import { deAccusative, deArticles, deRegular, deSeinHaben } from './german';
import { itArticles, itEssereAvere, itRegular } from './italian';
import { ptGender, ptRegular, ptSerEstar } from './portuguese';
import { PRONOUN_BANKS, pronounQuestions } from './pronouns';
import { toQuestion, type BuilderItem } from './builder';
import { tenseQuestions, topicId, type LanguageTenses, type Tense } from './tenses';
import { DE_TENSES } from './tensesDe';
import { EN_TENSES } from './tensesEn';
import { ES_TENSES } from './tensesEs';
import { FR_TENSES } from './tensesFr';
import { IT_TENSES } from './tensesIt';
import { PT_TENSES } from './tensesPt';

/** The non-Spanish topics: each one a generator. */
const OTHER: Partial<Record<TopicId, (cfg: GrammarConfig, n: number) => Question[]>> = {
  fr_er: (_c, n) => frEr(n), fr_ir: (_c, n) => frIr(n), fr_re: (_c, n) => frRe(n),
  fr_etre_avoir: (_c, n) => frEtreAvoir(n), fr_passe_compose: (_c, n) => frPasseCompose(n), fr_gender: (_c, n) => frGender(n),
  de_present: (_c, n) => deRegular(n), de_sein_haben: (_c, n) => deSeinHaben(n),
  de_articles: (_c, n) => deArticles(n), de_accusative: (_c, n) => deAccusative(n),
  it_present: (_c, n) => itRegular(n), it_essere_avere: (_c, n) => itEssereAvere(n), it_articles: (_c, n) => itArticles(n),
  pt_present: (c, n) => ptRegular(c, n), pt_ser_estar: (_c, n) => ptSerEstar(n), pt_gender: (_c, n) => ptGender(n),
  en_be: (_c, n) => enBe(n), en_present_s: (_c, n) => enPresentS(n), en_past_regular: (_c, n) => enPastRegular(n),
  en_past_irregular: (_c, n) => enPastIrregular(n), en_articles: (_c, n) => enArticles(n),
  ...Object.fromEntries(PRONOUN_BANKS.map(b => [b.topic, (_c: GrammarConfig, n: number) => pronounQuestions(b, n)])),
};

export { shuffle };

// ─────────────────────────────────────────────────────────────────────────────
// Grammar topics the teacher can assign, and the questions each one makes.
// Everything is generated by rule from the teacher's settings — no AI calls —
// so a lesson is different every time but always correct.
// ─────────────────────────────────────────────────────────────────────────────

export type TopicId =
  | 'present_regular' | 'present_stem' | 'preterite_regular' | 'gender' | 'ser_estar' | 'gustar'
  | 'fr_er' | 'fr_ir' | 'fr_re' | 'fr_etre_avoir' | 'fr_passe_compose' | 'fr_gender'
  | 'de_present' | 'de_sein_haben' | 'de_articles' | 'de_accusative'
  | 'it_present' | 'it_essere_avere' | 'it_articles'
  | 'pt_present' | 'pt_ser_estar' | 'pt_gender'
  | 'en_be' | 'en_present_s' | 'en_past_regular' | 'en_past_irregular' | 'en_articles'
  | 'es_do' | 'es_io' | 'es_double' | 'fr_do' | 'fr_io' | 'it_do' | 'it_io' | 'pt_do' | 'pt_io'
  | 'de_akk_pron' | 'de_dat_pron' | 'en_obj_pron'
  | 'custom'
  | `${string}_t_${string}`;

export type GrammarConfig = {
  topics: TopicId[];
  /** Verbs for each conjugation topic; empty = the defaults. */
  verbs?: Partial<Record<'present_regular' | 'present_stem' | 'preterite_regular', string[]>>;
  vosotros?: boolean;
  /** Old teacher-written lines: "prompt = answer" or "prompt = right | wrong | wrong". Replaced by `builder`. */
  custom?: string;
  /** The Custom Lesson Builder's questions, in the teacher's order. */
  builder?: BuilderItem[];
  /** Questions per lesson. */
  length?: number;
  /** 1–5 (see LEVELS); the level of the first lesson. Unset = 3. */
  difficulty?: number;
  /** If set, lessons climb from `difficulty` to this level across the required lessons. */
  difficultyTo?: number;
};

// ── Difficulty ──────────────────────────────────────────────────────────────
// A level sets how much is typed (vs. multiple choice), how many choices a
// multiple-choice question offers, and whether a missing accent still counts.

export type Level = { n: number; name: string; typed: number; choices: number; strictAccents: boolean; summary: string };

export const LEVELS: Level[] = [
  { n: 1, name: 'Easiest', typed: 0, choices: 2, strictAccents: false, summary: 'All multiple choice, 2 options each' },
  { n: 2, name: 'Easy', typed: 0.25, choices: 3, strictAccents: false, summary: '¼ typed, 3 options each' },
  { n: 3, name: 'Medium', typed: 0.5, choices: 4, strictAccents: false, summary: 'Half typed, 4 options each' },
  { n: 4, name: 'Hard', typed: 0.75, choices: 4, strictAccents: false, summary: '¾ typed, 4 options each' },
  { n: 5, name: 'Hardest', typed: 1, choices: 4, strictAccents: true, summary: 'Everything typed, and accents must be right' },
];

export const levelInfo = (n: number) => LEVELS[Math.min(Math.max(Math.round(n), 1), 5) - 1];

/**
 * The level for a student's next lesson. `lesson` is 0-based (how many they've
 * finished); with a climb, lesson 1 is at the start level, the last required
 * lesson at the end level, evenly in between, and extra lessons stay at the end.
 */
export function levelFor(cfg: GrammarConfig, lesson: number, required: number): number {
  const from = cfg.difficulty ?? 3;
  const to = cfg.difficultyTo;
  if (to == null || to === from || required <= 1) return from;
  const k = Math.min(Math.max(lesson, 0), required - 1);
  return Math.round(from + ((to - from) * k) / (required - 1));
}

/** Swap an instruction's wording when a question changes between typed and choice. */
function retitle(instruction: string, kind: Question['kind']): string {
  const typed = kind === 'type';
  const pairs: [string, string][] = [
    ['write the verb', 'choose the verb'],
    ['Write ser or estar, conjugated', 'Ser or estar?'],
    ['Write the answer', 'Choose the answer'],
    ['Write the pronoun', 'Choose the pronoun'],
    ['write the pronoun', 'choose the pronoun'],
    ['Write the article', 'Choose the article'],
    ['Write the missing words', 'Choose the missing words'],
  ];
  for (const [t, c] of pairs) {
    if (typed && instruction.includes(c)) return instruction.replace(c, t);
    if (!typed && instruction.includes(t)) return instruction.replace(t, c);
  }
  return instruction;
}

/** Make the lesson match a level: exactly its share typed, and its number of choices. */
function applyLevel(qs: Question[], level: Level): Question[] {
  // A question with no wrong options to offer (your own "prompt = answer" lines) can only be typed.
  // Teacher-built questions keep the format the teacher chose; the level applies to the rest.
  const free = qs.filter(q => !q.fixed);
  const canChoose = (q: Question) => (q.options ?? []).some(o => !q.answers.includes(o));
  const forced = free.filter(q => !canChoose(q)).length;
  let typedLeft = Math.max(Math.round(level.typed * free.length) - forced, 0);
  const typedKeys = new Set<string>();
  for (const q of shuffle(free.filter(canChoose))) {
    if (typedLeft-- <= 0) break;
    typedKeys.add(q.key);
  }
  return qs.map(q => {
    if (q.fixed) return { ...q, strictAccents: level.strictAccents };
    const kind: Question['kind'] = !canChoose(q) || typedKeys.has(q.key) ? 'type' : 'choice';
    const wrong = (q.options ?? []).filter(o => !q.answers.includes(o));
    return {
      ...q, kind, instruction: retitle(q.instruction, kind), strictAccents: level.strictAccents,
      // Wrong options keep their generator's order, so the best traps survive a 2-option level.
      options: kind === 'choice' ? shuffle([q.answers[0], ...wrong.slice(0, level.choices - 1)]) : undefined,
    };
  });
}

export type Question = {
  key: string;
  topic: TopicId;
  /** choice: tap an option · type: write it · order: tap word tiles into a sentence · match: drag each pronoun onto its form */
  kind: 'choice' | 'type' | 'order' | 'match';
  /** Small line above, e.g. "Present tense". */
  instruction: string;
  /** The big line. "___" is drawn as a blank. */
  prompt: string;
  /** Under the prompt, e.g. "to speak". */
  hint?: string;
  options?: string[];
  /** Accepted answers (first is the one shown as correct). */
  answers: string[];
  /** The rule behind the answer, shown in the feedback banner. */
  why?: string;
  /** Hardest level: a missing or wrong accent counts as wrong. */
  strictAccents?: boolean;
  /** Build the sentence: the word tiles, shuffled. Match: the pronoun chips, shuffled. */
  tiles?: string[];
  /** Match: every person of one verb, in the order the forms are shown. Each chip goes on its form. */
  pairs?: { who: string; form: string }[];
  /** A teacher-built question: difficulty never changes its format. */
  fixed?: boolean;
};

/** `langs`: the class languages a topic exists for. Built-ins are Spanish; your own questions work in any language. */
export type TopicInfo = { title: string; blurb: string; emoji: string; langs?: string[]; tense?: boolean };

const BASE_TOPICS: Record<string, TopicInfo> = {
  present_regular: { title: 'Present tense: regular verbs', blurb: 'hablo, comes, viven…', emoji: '🗣️', langs: ['es'] },
  present_stem: { title: 'Present tense: stem-changing verbs', blurb: 'pienso, puedo, pido, juego…', emoji: '👢', langs: ['es'] },
  preterite_regular: { title: 'Preterite: regular verbs', blurb: 'hablé, comiste, vivieron…', emoji: '⏪', langs: ['es'] },
  gender: { title: 'Noun gender: el / la', blurb: 'el problema, la mano, el día…', emoji: '⚖️', langs: ['es'] },
  ser_estar: { title: 'Ser vs. estar', blurb: 'es doctora, está cansada, la fiesta es en…', emoji: '🔀', langs: ['es'] },
  gustar: { title: 'Gustar', blurb: 'me gusta, te gustan, les gusta…', emoji: '❤️', langs: ['es'] },

  fr_er: { title: 'Present tense: -er verbs', blurb: 'je parle, nous mangeons, j’aime…', emoji: '🗣️', langs: ['fr'] },
  fr_ir: { title: 'Present tense: -ir verbs', blurb: 'je finis, nous finissons…', emoji: '🏁', langs: ['fr'] },
  fr_re: { title: 'Present tense: -re verbs', blurb: 'je vends, il attend, nous répondons…', emoji: '🔁', langs: ['fr'] },
  fr_etre_avoir: { title: 'Être & avoir', blurb: 'je suis, tu as, nous sommes…', emoji: '⭐', langs: ['fr'] },
  fr_passe_compose: { title: 'Passé composé: avoir or être?', blurb: 'je suis allé(e), j’ai mangé…', emoji: '⏪', langs: ['fr'] },
  fr_gender: { title: 'Gender: un / une', blurb: 'un problème, une plage, un musée…', emoji: '⚖️', langs: ['fr'] },

  de_present: { title: 'Present tense: regular verbs', blurb: 'ich spiele, du arbeitest, du tanzt…', emoji: '🗣️', langs: ['de'] },
  de_sein_haben: { title: 'Sein & haben', blurb: 'ich bin, du hast, ihr seid…', emoji: '⭐', langs: ['de'] },
  de_articles: { title: 'Der, die, das', blurb: 'der Löffel, die Gabel, das Mädchen…', emoji: '⚖️', langs: ['de'] },
  de_accusative: { title: 'Accusative: den / die / das', blurb: 'Ich sehe den Hund…', emoji: '🎯', langs: ['de'] },

  it_present: { title: 'Present tense: -are / -ere / -ire', blurb: 'parlo, cerchi, studiamo, dormono…', emoji: '🗣️', langs: ['it'] },
  it_essere_avere: { title: 'Essere & avere', blurb: 'sono, hai, abbiamo…', emoji: '⭐', langs: ['it'] },
  it_articles: { title: 'Articles: il / lo / la / l’', blurb: 'lo studente, l’amico, il problema…', emoji: '⚖️', langs: ['it'] },

  pt_present: { title: 'Present tense: regular verbs', blurb: 'eu falo, você come, nós partimos…', emoji: '🗣️', langs: ['pt'] },
  pt_ser_estar: { title: 'Ser vs. estar', blurb: 'sou professor, estou cansado…', emoji: '🔀', langs: ['pt'] },
  pt_gender: { title: 'Gender: o / a', blurb: 'o problema, a mão, a viagem…', emoji: '⚖️', langs: ['pt'] },

  en_be: { title: 'To be: am / is / are', blurb: 'I am, she is, they are…', emoji: '⭐', langs: ['en'] },
  en_present_s: { title: 'Present simple: he / she -s', blurb: 'she plays, he watches, it has…', emoji: '🗣️', langs: ['en'] },
  en_past_regular: { title: 'Past simple: regular verbs', blurb: 'played, studied, stopped…', emoji: '⏪', langs: ['en'] },
  en_past_irregular: { title: 'Past simple: irregular verbs', blurb: 'went, ate, bought, thought…', emoji: '🌀', langs: ['en'] },
  en_articles: { title: 'A or an?', blurb: 'an hour, a university, an apple…', emoji: '🅰️', langs: ['en'] },
  es_do: { title: 'Direct object pronouns', blurb: 'lo, la, los, las, me, te, nos', emoji: '🎯', langs: ['es'] },
  es_io: { title: 'Indirect object pronouns', blurb: 'le, les, me, te, nos', emoji: '🎁', langs: ['es'] },
  es_double: { title: 'Double object pronouns', blurb: 'se lo, te la, me los…', emoji: '🔗', langs: ['es'] },
  fr_do: { title: 'Direct object pronouns', blurb: 'le, la, les, me, te, nous, vous', emoji: '🎯', langs: ['fr'] },
  fr_io: { title: 'Indirect object pronouns', blurb: 'lui, leur, me, te, nous, vous', emoji: '🎁', langs: ['fr'] },
  it_do: { title: 'Direct object pronouns', blurb: 'lo, la, li, le, mi, ti, ci, vi', emoji: '🎯', langs: ['it'] },
  it_io: { title: 'Indirect object pronouns', blurb: 'gli, le, mi, ti, ci, vi', emoji: '🎁', langs: ['it'] },
  pt_do: { title: 'Direct object pronouns', blurb: 'o, a, os, as, me, nos (written standard)', emoji: '🎯', langs: ['pt'] },
  pt_io: { title: 'Indirect object pronouns', blurb: 'lhe, lhes, me, nos (written standard)', emoji: '🎁', langs: ['pt'] },
  de_akk_pron: { title: 'Accusative pronouns', blurb: 'mich, dich, ihn, sie, es, uns, euch', emoji: '🎯', langs: ['de'] },
  de_dat_pron: { title: 'Dative pronouns', blurb: 'mir, dir, ihm, ihr, uns, euch, ihnen', emoji: '🎁', langs: ['de'] },
  en_obj_pron: { title: 'Object pronouns', blurb: 'me, him, her, us, them (not I, he, she…)', emoji: '🎯', langs: ['en'] },
  custom: { title: 'Custom Lesson Builder', blurb: 'Your own questions: multiple choice, fill in the blank, build the sentence…', emoji: '🧩' },
};

/** Topics that are tenses in their own right (shown under "Verb tenses"). */
const TENSE_LIKE = new Set([
  'present_regular', 'present_stem', 'preterite_regular', 'fr_er', 'fr_ir', 'fr_re', 'de_present', 'it_present',
  'pt_present', 'en_present_s', 'en_past_regular', 'en_past_irregular',
]);

const TENSE_LANGS: LanguageTenses[] = [ES_TENSES, EN_TENSES, FR_TENSES, DE_TENSES, IT_TENSES, PT_TENSES];
export const TENSE_BY_ID = new Map<string, { L: LanguageTenses; T: Tense }>();
for (const L of TENSE_LANGS) for (const T of L.tenses) TENSE_BY_ID.set(topicId(L.lang, T.id), { L, T });

export const TOPICS: Record<string, TopicInfo> = {
  ...Object.fromEntries(Object.entries(BASE_TOPICS).map(([k, v]) => [k, TENSE_LIKE.has(k) ? { ...v, tense: true } : v])),
  ...Object.fromEntries([...TENSE_BY_ID].map(([id, { L, T }]) => [id, {
    title: T.title, blurb: T.blurb, emoji: '⏳', langs: [L.lang], tense: true,
  }])),
};

/** Topics available to a class teaching `language`. */
export function topicsFor(language: string): TopicId[] {
  return (Object.keys(TOPICS) as TopicId[]).filter(t => !TOPICS[t].langs || TOPICS[t].langs!.includes(language));
}

export const DEFAULT_VERBS = {
  present_regular: DEFAULT_REGULAR,
  present_stem: DEFAULT_STEM,
  preterite_regular: DEFAULT_PRETERITE,
};

/** Problems with a teacher's verb list, so the form can say so before saving. */
export function verbProblems(topic: keyof typeof DEFAULT_VERBS, verbs: string[]): string[] {
  const out: string[] = [];
  for (const v of verbs) {
    if (!endingOf(v)) out.push(`“${v}” isn't an infinitive (-ar, -er, -ir).`);
    else if (topic === 'present_regular' && (IRREGULAR.has(v) || STEM_CHANGERS[v])) out.push(`“${v}” isn't regular in the present.`);
    else if (topic === 'present_stem' && !STEM_CHANGERS[v]) out.push(`“${v}” isn't a stem-changing verb this topic knows.`);
    else if (topic === 'preterite_regular' && PRETERITE_IRREGULAR.has(v)) out.push(`“${v}” isn't regular in the preterite.`);
  }
  return out;
}

export function parseVerbList(s: string): string[] {
  return [...new Set(s.toLowerCase().split(/[\s,;]+/).map(v => v.trim()).filter(Boolean))];
}

type Custom = { prompt: string; answers: string[]; wrong: string[] };

/** "Yo ___ estudiante. = soy" · "Yo ___ cansado. = estoy | soy | es" · alternatives with " / ". */
export function parseCustom(s: string | undefined): Custom[] {
  if (!s) return [];
  const out: Custom[] = [];
  for (const raw of s.split('\n')) {
    const line = raw.trim();
    const i = line.lastIndexOf('=');
    if (!line || i < 1) continue;
    const prompt = line.slice(0, i).trim();
    const parts = line.slice(i + 1).split('|').map(x => x.trim()).filter(Boolean);
    if (!prompt || parts.length === 0) continue;
    out.push({ prompt, answers: parts[0].split('/').map(x => x.trim()).filter(Boolean), wrong: parts.slice(1) });
  }
  return out;
}

// ── Generation ──────────────────────────────────────────────────────────────

function people(cfg: GrammarConfig): Person[] {
  return cfg.vosotros ? PEOPLE : PEOPLE.filter(p => p !== 'vosotros');
}

function conjugation(
  topic: 'present_regular' | 'present_stem' | 'preterite_regular', cfg: GrammarConfig, n: number,
): Question[] {
  const verbs = cfg.verbs?.[topic]?.length ? cfg.verbs[topic]! : DEFAULT_VERBS[topic];
  const form = topic === 'preterite_regular' ? preterite : present;
  const tense = topic === 'preterite_regular' ? 'Preterite (past)' : 'Present tense';
  const ps = people(cfg);
  const qs: Question[] = [];
  for (let k = 0; k < n; k++) {
    const verb = pick(verbs);
    const p = pick(ps);
    const answer = form(verb, p);
    if (!answer) continue;
    const who = pick(PERSON_LABEL[p]);
    const asType = Math.random() < 0.5;
    // Distractors that test the actual skill: other people's forms, and for
    // stem changers the form WITHOUT the change; for the preterite, the present.
    const wrong = new Set<string>();
    for (const q of shuffle(ps.filter(x => x !== p))) wrong.add(form(verb, q)!);
    if (topic === 'present_stem') wrong.add(presentUnchanged(verb, p)!);
    if (topic === 'preterite_regular') wrong.add(present(verb, p)!);
    wrong.delete(answer);
    const options = shuffle([answer, ...shuffle([...wrong]).slice(0, 3)]);
    qs.push({
      key: `${topic}:${verb}:${p}`, topic, kind: asType ? 'type' : 'choice',
      instruction: `${tense} · ${asType ? 'write' : 'choose'} the verb`,
      prompt: `${who} ___ (${verb})`, hint: ENGLISH[verb], options, answers: [answer],
    });
  }
  return qs;
}

function gender(n: number): Question[] {
  const traps = NOUNS.filter(x => x.trap);
  const plain = NOUNS.filter(x => !x.trap);
  const qs: Question[] = [];
  for (let k = 0; k < n; k++) {
    const noun = Math.random() < 0.45 ? pick(traps) : pick(plain);
    qs.push({
      key: `gender:${noun.w}`, topic: 'gender', kind: 'choice', instruction: 'Choose the article',
      prompt: `___ ${noun.w}`, hint: noun.en, options: ['el', 'la'], answers: [article(noun)],
    });
  }
  return qs;
}

function serEstar(n: number): Question[] {
  const traps = SER_ESTAR.filter(x => x.trap);
  const plain = SER_ESTAR.filter(x => !x.trap);
  const qs: Question[] = [];
  for (let k = 0; k < n; k++) {
    const x = Math.random() < 0.3 ? pick(traps) : pick(plain);
    const answer = x.use === 'ser' ? x.ser : x.estar;
    const typed = Math.random() < 0.3;
    qs.push({
      key: `ser_estar:${x.s}`, topic: 'ser_estar', kind: typed ? 'type' : 'choice',
      instruction: typed ? 'Write ser or estar, conjugated' : 'Ser or estar?',
      prompt: x.s, hint: x.en, why: x.why, answers: [answer],
      options: shuffle([x.ser, x.estar]),
    });
  }
  return qs;
}

function gustar(cfg: GrammarConfig, n: number): Question[] {
  const likers = cfg.vosotros ? LIKERS : LIKERS.filter(l => l.pron !== 'os');
  const pronouns = cfg.vosotros ? PRONOUNS : PRONOUNS.filter(p => p !== 'os');
  const qs: Question[] = [];
  for (let k = 0; k < n; k++) {
    const who = pick(likers);
    const what = pick(LIKED);
    const verb = what.plural ? 'gustan' : 'gusta';
    const en = `${who.en} ${what.en}.`;
    const verbWhy = what.plural ? `More than one thing (${what.es}) → gustan`
      : what.action ? `An action (${what.es}) → gusta` : `One thing (${what.es}) → gusta`;
    const r = Math.random();
    if (r < 0.45) {
      qs.push({
        key: `gustar:v:${who.a}:${what.es}`, topic: 'gustar', kind: 'choice', instruction: 'Gusta or gustan?',
        prompt: `${who.a} ${who.pron} ___ ${what.es}.`, hint: en, why: verbWhy,
        options: ['gusta', 'gustan'], answers: [verb],
      });
    } else if (r < 0.8) {
      qs.push({
        key: `gustar:p:${who.a}:${what.es}`, topic: 'gustar', kind: 'choice', instruction: 'Choose the pronoun',
        prompt: `${who.a} ___ ${verb} ${what.es}.`, hint: en, why: `${who.a} → ${who.pron}`,
        options: shuffle([who.pron, ...shuffle(pronouns.filter(p => p !== who.pron)).slice(0, 3)]), answers: [who.pron],
      });
    } else {
      qs.push({
        key: `gustar:t:${who.a}:${what.es}`, topic: 'gustar', kind: 'type', instruction: 'Write the missing words',
        prompt: `${who.a} ___ ${what.es}.`, hint: en, why: `${who.a} → ${who.pron}; ${verbWhy.toLowerCase()}`,
        answers: [`${who.pron} ${verb}`],
        options: shuffle([`${who.pron} ${verb}`, `${who.pron} ${what.plural ? 'gusta' : 'gustan'}`,
          ...shuffle(pronouns.filter(p => p !== who.pron)).slice(0, 2).map(p => `${p} ${verb}`)]),
      });
    }
  }
  return qs;
}

/** The teacher's own questions: the builder's, or (older assignments) the text lines. */
function customQuestions(cfg: GrammarConfig): Question[] {
  if (cfg.builder?.length) return cfg.builder.map(toQuestion).filter((q): q is Question => !!q);
  return parseCustom(cfg.custom).map((c, i) => (c.wrong.length
    ? {
      key: `custom:${i}:${c.prompt}`, topic: 'custom' as const, kind: 'choice' as const, fixed: true,
      instruction: 'Choose the answer', prompt: c.prompt,
      options: shuffle([c.answers[0], ...c.wrong]), answers: c.answers,
    }
    : {
      key: `custom:${i}:${c.prompt}`, topic: 'custom' as const, kind: 'type' as const, fixed: true,
      instruction: 'Write the answer', prompt: c.prompt, answers: c.answers,
    }));
}

/** True when the lesson is only the teacher's own questions: then it's all of them, in their order. */
export const builderOnly = (cfg: GrammarConfig) => cfg.topics.length === 1 && cfg.topics[0] === 'custom';

/** Questions in one lesson. */
export function lessonSize(cfg: GrammarConfig): number {
  if (builderOnly(cfg)) return customQuestions(cfg).length;
  return Math.min(Math.max(cfg.length ?? 10, 5), 20);
}

/** One lesson at a difficulty level: the questions shared out across the assigned topics, mixed. */
export function buildLesson(cfg: GrammarConfig, level = cfg.difficulty ?? 3): Question[] {
  const level_ = levelInfo(level);
  // Only the teacher's own questions: every one, in the order they arranged them.
  if (builderOnly(cfg)) return applyLevel(customQuestions(cfg), level_);
  const length = Math.min(Math.max(cfg.length ?? 10, 5), 20);
  const custom = customQuestions(cfg);
  const topics = cfg.topics.filter(t => t !== 'custom' || custom.length > 0);
  if (topics.length === 0) return [];
  const per = Math.ceil(length / topics.length);
  const all: Question[] = [];
  for (const t of topics) {
    if (t === 'gender') all.push(...gender(per));
    else if (t === 'ser_estar') all.push(...serEstar(per));
    else if (t === 'gustar') all.push(...gustar(cfg, per));
    else if (t === 'custom') all.push(...shuffle(custom).slice(0, per));
    else if (t === 'present_regular' || t === 'present_stem' || t === 'preterite_regular') all.push(...conjugation(t, cfg, per));
    else if (TENSE_BY_ID.has(t)) { const { L, T } = TENSE_BY_ID.get(t)!; all.push(...tenseQuestions(L, T, cfg, per)); }
    else all.push(...(OTHER[t]?.(cfg, per) ?? []));
  }
  // No exact repeats within a lesson.
  const seen = new Set<string>();
  const unique = all.filter(q => (seen.has(q.key) ? false : (seen.add(q.key), true)));
  const lesson = applyLevel(shuffle(unique).slice(0, length), level_);
  // Each verb starts with its chart: match every pronoun to its form, right before the first
  // question about that verb. Charts for assigned verbs this lesson doesn't ask about come first.
  const boards = matchBoards(cfg, lesson);
  const boardFor = (q: Question) => boards.find(b => b.topic === q.topic && b.key.split(':')[2] === q.key.split(':')[1]);
  const used = new Set<Question>();
  const out: Question[] = boards.filter(b => !lesson.some(q => boardFor(q) === b));
  out.forEach(b => used.add(b));
  for (const q of lesson) {
    const b = boardFor(q);
    if (b && !used.has(b)) { used.add(b); out.push(b); }
    out.push(q);
  }
  return out;
}

// ── Matching the pronouns ───────────────────────────────────────────────────
// One board per verb: every person's pronoun dragged onto that person's form
// (Josiah, 2026-10-22), placed right before the lesson's first question about
// that verb, so each verb starts with filling in its chart. The verbs are the teacher's own list for a topic when
// they gave one, otherwise the verbs this lesson asked about (so a topic left
// on its default list doesn't turn into fifteen boards).

const CONJUGATING = ['present_regular', 'present_stem', 'preterite_regular'] as const;

/** Does a lesson with these settings end with pronoun matching? */
export const hasMatching = (cfg: GrammarConfig) => cfg.topics.some(t => (CONJUGATING as readonly string[]).includes(t) || TENSE_BY_ID.has(t));

function matchBoards(cfg: GrammarConfig, lesson: Question[]): Question[] {
  const asked = (topic: string) => [...new Set(lesson.filter(q => q.topic === topic).map(q => q.key.split(':')[1]).filter(Boolean))];
  const out: Question[] = [];
  for (const t of cfg.topics) {
    if ((CONJUGATING as readonly string[]).includes(t)) {
      const topic = t as (typeof CONJUGATING)[number];
      const form = topic === 'preterite_regular' ? preterite : present;
      const tense = topic === 'preterite_regular' ? 'Preterite (past)' : 'Present tense';
      const verbs = cfg.verbs?.[topic]?.length ? cfg.verbs[topic]! : asked(topic);
      for (const verb of verbs) {
        const pairs = people(cfg).flatMap(p => { const f = form(verb, p); return f ? [{ who: PERSON_LABEL[p].join(' / '), form: f }] : []; });
        if (new Set(pairs.map(p => p.form)).size >= 2) out.push(board(`match:${topic}:${verb}`, topic, tense, verb, ENGLISH[verb], pairs));
      }
    } else if (TENSE_BY_ID.has(t)) {
      const { L, T } = TENSE_BY_ID.get(t)!;
      const idx = (T.people ?? L.people.map((_, i) => i)).filter(i => cfg.vosotros || i !== L.optional);
      for (const verb of asked(t)) {
        const pairs = idx.flatMap(i => {
          const f = T.forms(verb, i)?.[0];
          if (!f) return [];
          const base = L.people[i].join(' / ');
          return [{ who: T.who ? T.who(i, f, base) : base, form: f }];
        });
        if (new Set(pairs.map(p => p.form)).size >= 2) out.push(board(`match:${t}:${verb}`, t, T.title, verb, T.verbs[verb], pairs));
      }
    }
  }
  return out;
}

function board(key: string, topic: TopicId, tense: string, verb: string, hint: string | undefined, pairs: { who: string; form: string }[]): Question {
  const shown = shuffle(pairs);
  return {
    key, topic, kind: 'match', fixed: true,
    instruction: `${tense} · match each pronoun to its form`,
    prompt: verb, hint, pairs: shown,
    tiles: shuffle(shown.map(p => p.who)),
    answers: [pairs.map(p => `${p.who} ${p.form}`).join(' · ')],
  };
}

// ── Checking ────────────────────────────────────────────────────────────────

const norm = (s: string) => s.toLowerCase().replace(/[¿?¡!.,;:"“”«»؟،]/g, '').replace(/\s+/g, ' ').trim();
const noAccents = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ñ/g, 'n');

/** right · accent (right but an accent missing/wrong — counted, with a note) · wrong */
export function check(q: Question, given: string): 'right' | 'accent' | 'wrong' {
  const g = norm(given);
  if (q.answers.some(a => norm(a) === g)) return 'right';
  if (q.kind === 'type' && !q.strictAccents && q.answers.some(a => noAccents(norm(a)) === noAccents(g))) return 'accent';
  return 'wrong';
}
